// ═══════════════════════════════════════════════════════════════
// PLANTILLAS DE RESPUESTA DE CASO — Snippets reutilizables
// ═══════════════════════════════════════════════════════════════
//
// Text snippets que el operador puede insertar al agregar comentarios
// (públicos o internos) en el detalle del caso. Soportan placeholders
// simples: {{numero}}, {{nombreEmpresa}}, {{tituloCaso}}.
//
// Schema:
//   {
//     codigo, nombre, contenido,
//     tiposCasoAplicables: ['reclamo-ml','*'],
//     categorias: ['logistica','comunicacion'],  // opcional, para filtrar
//     visibilidadDefault: 'interno'|'publico',
//     activo, orden
//   }
// ═══════════════════════════════════════════════════════════════

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc,
} from 'firebase/firestore'
import { db } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'
import { forPlatformCreate, forUpdate } from './dbHelpers'

// ──────── ABM ────────

export async function listarPlantillasCaso({ soloActivas = true, tipoCasoCodigo = null, categoria = null } = {}) {
  const snap = await getDocs(collection(db, COLLECTIONS.PLANTILLAS_RESPUESTA_CASO))
  let list = snap.docs.map(docConId)
  if (soloActivas) list = list.filter(p => p.activo !== false)
  if (tipoCasoCodigo) {
    list = list.filter(p =>
      !p.tiposCasoAplicables?.length ||
      p.tiposCasoAplicables.includes(tipoCasoCodigo) ||
      p.tiposCasoAplicables.includes('*')
    )
  }
  if (categoria) {
    list = list.filter(p =>
      !p.categorias?.length ||
      p.categorias.includes(categoria) ||
      p.categorias.includes('*')
    )
  }
  list.sort((a, b) => (a.orden || 0) - (b.orden || 0))
  return list
}

export async function getPlantillaCaso(plantillaId) {
  const snap = await getDoc(doc(db, COLLECTIONS.PLANTILLAS_RESPUESTA_CASO, plantillaId))
  return snap.exists() ? docConId(snap) : null
}

export async function upsertPlantillaCaso(plantillaId, datos) {
  const data = {
    codigo: datos.codigo || null,
    nombre: datos.nombre || 'Plantilla',
    contenido: datos.contenido || '',
    tiposCasoAplicables: Array.isArray(datos.tiposCasoAplicables) ? datos.tiposCasoAplicables : ['*'],
    categorias: Array.isArray(datos.categorias) ? datos.categorias : [],
    visibilidadDefault: datos.visibilidadDefault || 'interno',
    activo: datos.activo !== false,
    orden: Number(datos.orden) || 100,
  }
  if (!plantillaId) {
    const payload = forPlatformCreate(data)
    const ref = await addDoc(collection(db, COLLECTIONS.PLANTILLAS_RESPUESTA_CASO), payload)
    return { id: ref.id, ...payload }
  }
  const payload = forUpdate(data)
  await updateDoc(doc(db, COLLECTIONS.PLANTILLAS_RESPUESTA_CASO, plantillaId), payload)
  return { id: plantillaId, ...payload }
}

// ──────── Render con placeholders ────────

/**
 * Reemplaza placeholders simples en el contenido de una plantilla.
 * Soportados: {{numero}}, {{nombreEmpresa}}, {{tituloCaso}}, {{categoria}}.
 *
 * @param {string} contenido — texto crudo de la plantilla
 * @param {object} ctx — { caso, empresa? }
 * @returns {string}
 */
export function renderPlantilla(contenido, ctx = {}) {
  if (!contenido) return ''
  const caso = ctx.caso || {}
  const empresa = ctx.empresa || {}
  return String(contenido)
    .replaceAll('{{numero}}', caso.numero || '')
    .replaceAll('{{nombreEmpresa}}', empresa.nombre || empresa.razonSocial || '')
    .replaceAll('{{tituloCaso}}', caso.titulo || '')
    .replaceAll('{{categoria}}', caso.categoria || '')
    .replaceAll('{{tipo}}', caso.tipo || '')
}

// ──────── Window debug ────────
if (typeof window !== 'undefined') {
  window.__plantillasCaso = {
    listarPlantillasCaso, getPlantillaCaso, upsertPlantillaCaso, renderPlantilla,
  }
}
